import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarProcedimientos, deleteProcedimientoFactura } from "../../../redux/actions";
import { ProcedimientosNuevosBilling, ProcedimientosDeleteBilling } from "../../../Service/Service.billing";
import {
    claveDe,
    guardar,
    invalidar,
    leer,
    traerCatalogo,
    traerPendientes,
    traerPromociones,
} from "../../../utils/cachePanelAgregar";
import { tituloClinico } from "../../../utils/textoClinico";
import { montoLegible } from "../../../utils/facturaCrear";
import {
    accionMasiva,
    contadorCoincidencias,
    filtrarPorCategoria,
    filtrarPorTexto,
    idPendiente,
    idTarifa,
    importeDe,
    filtrarPorTipo,
    tiposDe,
    payloadProcedimientosNuevos,
    yaFacturado,
    idPromocion,
    detallesDe,
    filtrarPromociones,
    importePromocion,
    payloadPromocion,
} from "../../../utils/panelAgregar";
import FilaAgregar from "./FilaAgregar";
import FilaPromocion from "./FilaPromocion";
import ListaVirtual from "./ListaVirtual";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import "../../../styles/FacturaCrear.css";
import "../../../styles/PanelAgregar.css";

/* ============================================================================
   CREAR FACTURA · FASE 1 — panel de agregar procedimientos

   Sustituye a tres modales que hacían lo mismo con tres diseños distintos:
   Procedimientos pendientes, Agregar nuevo Procedimiento (catálogo) y Agregar
   Promoción. Los tres traen líneas a la factura desde un origen diferente; no
   había razón para que fueran tres pantallas.

   POR QUÉ PANEL Y NO MODAL: un modal tapa la factura. Este se abre a la
   derecha, 566px, sin velo oscuro, así que mientras marcas ves crecer la
   lista y subir el total. Eso es lo que cambia la sensación de control.

   LA REGLA DE LA PESTAÑA DE PENDIENTES: marcar agrega EN EL ACTO y desmarcar
   quita; el panel se queda abierto. Se puede porque agregar un pendiente es
   sólo estado de redux -- la prestación ya existe en la base. Con eso muere
   el "agregué 380 y ahora los borro de a uno": desmarcar cuesta lo mismo que
   marcar.

   Catálogo y Promociones NO se comportan así, y es una decisión, no un olvido:
   allí agregar CREA filas en `procedimiento_cita_paciente` vía
   POST /billing/procedures/news. Marcar por error dejaría basura permanente,
   así que en esas pestañas el marcado es local y hay un botón que agrega todo
   lo marcado de una vez. Llegan en el siguiente paso de esta fase.

   NADA DE LÓGICA CAMBIA: el endpoint de pendientes, sus parámetros y su
   respuesta son los mismos; agregar y quitar despachan las mismas acciones de
   redux que despachaba el modal viejo.
   ========================================================================== */

const ETIQUETA_PESTANA = {
    pendientes: "los pendientes",
    catalogo: "el catálogo",
    promociones: "las promociones",
};

const PESTANAS = [
    { id: "pendientes", etiqueta: "Pendientes" },
    { id: "catalogo", etiqueta: "Catálogo" },
    { id: "promociones", etiqueta: "Promociones" },
];

function IconoBuscar() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
    );
}

function IconoRefrescar() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 4v5h-5" />
        </svg>
    );
}

function IconoCerrar() {
    return (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
        </svg>
    );
}

export default function PanelAgregar({
    abierto,
    onCerrar,
    // Con que pestana se abre. Los tres botones de la cabecera de
    // procedimientos siguen existiendo (criterio 3: ninguna accion
    // desaparece) y cada uno abre el panel en SU pestana.
    pestanaInicial = "pendientes",
    pacienteSeleccionado,
    seguroSeleccionado,
    // Catálogo de categorías, ya cargado por la pantalla. Sin él el chip de
    // categoría no se pinta: no se pide aparte para no añadir una petición.
    categorias = [],
}) {
    const dispatch = useDispatch();
    const { mostrar } = useToast();
    // El `|| []` pegado al `useSelector` devuelve un array NUEVO en cada
    // render cuando el estado viene vacio, y eso hace inutiles los `useMemo`
    // de abajo. Se estabiliza aqui, una vez.
    const enFacturaCrudo = useSelector((state) => state.facturaredux.procedimientos);
    const enFactura = useMemo(() => enFacturaCrudo || [], [enFacturaCrudo]);

    const [pestana, setPestana] = useState(pestanaInicial);
    const [busqueda, setBusqueda] = useState("");
    const [categoria, setCategoria] = useState("");
    const [pendientes, setPendientes] = useState(null); // null = todavía no se pidió

    /* `cargando` y `error` son POR PESTAÑA, no compartidos. Con un solo par,
       un fallo al cargar el catálogo dejaba el mensaje de error puesto y al
       volver a Pendientes se veía el error en vez de las 380 filas que ya
       estaban cargadas. */
    const [cargando, setCargando] = useState({});
    const [error, setError] = useState({});

    const marcarCargando = useCallback(
        (cual, valor) => setCargando((previo) => ({ ...previo, [cual]: valor })),
        []
    );
    const marcarError = useCallback(
        (cual, valor) => setError((previo) => ({ ...previo, [cual]: valor })),
        []
    );

    /* -- pestaña Catálogo --
       Aquí marcar NO agrega: `seleccion` es un Set de
       `idprocedimiento_company_precios` que vive sólo en el panel, y no llega
       a la base hasta que se pulsa "Agregar los N". Es la decisión tomada con
       el usuario: agregar del catálogo CREA filas en
       `procedimiento_cita_paciente`, y marcar-desmarcar por error dejaría
       basura permanente. */
    const [catalogo, setCatalogo] = useState(null);
    const [seleccion, setSeleccion] = useState(() => new Set());
    const [tipo, setTipo] = useState("");
    const [agregando, setAgregando] = useState(false);

    /* -- pestaña Promociones --
       Se elige UNA, como en el modal viejo (que rechazaba explícitamente más
       de una). `promoAbierta` es sólo "qué grupo estoy mirando", que no es lo
       mismo que "cuál he elegido": abrir para ver qué trae no puede agregar
       44 líneas. */
    const [promociones, setPromociones] = useState(null);
    const [promoElegida, setPromoElegida] = useState(null);
    const [promoAbierta, setPromoAbierta] = useState(null);

    /* Descartar un pendiente. Es la accion que el modal viejo tenia como icono
       de papelera y que se hacia SIN preguntar: un `UPDATE ... SET estado =
       'Eliminado'` a la primera. Aqui se confirma, porque escribe en la base y
       no se deshace desde la interfaz. */
    const [porDescartar, setPorDescartar] = useState(null);
    const [descartando, setDescartando] = useState(false);
    /* Descartar EN LOTE. El paciente de pruebas tiene 382 pendientes: poder
       descartar de uno en uno es la misma trampa que el encargo denuncia para
       agregar y quitar. No hay endpoint de lote (PUT /billing/procedures/:id
       es uno por id), asi que son N peticiones con concurrencia limitada y
       progreso a la vista. */
    const [loteDescartar, setLoteDescartar] = useState(null); // items a descartar
    const [progresoLote, setProgresoLote] = useState(null); // { hechos, total, fallos }

    /* MODO DESCARTAR. En Pendientes la casilla significa "esta en la factura":
       marcar agrega. Para poder elegir 100 concretos y descartar ESOS hace
       falta un segundo significado, y dos significados no pueden compartir una
       casilla sin decir cual esta activo. De ahi el modo: mientras esta puesto,
       la casilla selecciona para descartar y NO agrega nada a la factura.

       Sin el modo sigue funcionando lo de antes: el filtro es la seleccion y se
       descarta todo lo que coincida. Los dos caminos conviven. */
    const [modoDescartar, setModoDescartar] = useState(false);
    const [seleccionDescartar, setSeleccionDescartar] = useState(() => new Set());

    const cajaRef = useRef(null);
    const buscadorRef = useRef(null);
    const origenFocoRef = useRef(null);
    const pedidoRef = useRef(null);
    const pedidoCatalogoRef = useRef(null);
    const pedidoPromosRef = useRef(null);

    /* -- carga -------------------------------------------------------------
       El guardián es un `ref` con la clave del paciente y se marca ANTES del
       `await`, no el estado `pendientes`. Con el estado no bastaba: entre
       abrir el panel y que llegue la respuesta, `pendientes` sigue en `null`,
       así que abrir-cerrar-reabrir rápido lanzaba DOS peticiones de 1.536 KB.
       Un ref se actualiza de forma sincrónica y cierra esa ventana. */
    const clavePeticion = claveDe(pacienteSeleccionado?.value, seguroSeleccionado?.value);

    const pedirPendientes = useCallback(async (forzar = false) => {
        if (!pacienteSeleccionado && !seguroSeleccionado) return;

        // FASE 4: si la precarga (o una apertura anterior) ya lo trajo, se
        // pinta y no se pide nada. Es lo que hace que cerrar y reabrir el
        // panel cueste CERO peticiones.
        if (!forzar) {
            const cacheado = leer(clavePeticion, "pendientes");
            if (Array.isArray(cacheado)) {
                setPendientes(cacheado);
                pedidoRef.current = clavePeticion;
                return;
            }
            // `null` en la caché = precarga EN VUELO. Marcarlo aquí evita
            // pedir por segunda vez lo mismo mientras llega.
            if (cacheado === null || pedidoRef.current === clavePeticion) return;
        }
        pedidoRef.current = clavePeticion;
        marcarCargando("pendientes", true);
        marcarError("pendientes", null);
        const { datos, error } = await traerPendientes(
            pacienteSeleccionado?.value,
            seguroSeleccionado?.value
        );
        // Si el paciente cambió mientras la respuesta viajaba, se DESCARTA:
        // una respuesta vieja no puede pisar la lista del paciente nuevo.
        if (pedidoRef.current !== clavePeticion) return;
        setPendientes(datos);
        marcarError("pendientes", error);
        if (!error) guardar(clavePeticion, "pendientes", datos);
        marcarCargando("pendientes", false);
    }, [pacienteSeleccionado, seguroSeleccionado, clavePeticion, marcarCargando, marcarError]);

    // Cambiar de paciente invalida lo cargado. Cerrar y reabrir el panel, o
    // cambiar de pestaña y volver, NO vuelve a pedir nada.
    useEffect(() => {
        pedidoRef.current = null;
        setPendientes(null);
        setError({});
        setModoDescartar(false);
        setSeleccionDescartar(new Set());
    }, [clavePeticion]);

    // El modo es de Pendientes: salir de la pestaña lo apaga, para no dejar una
    // selección destructiva viva donde no se ve.
    useEffect(() => {
        if (pestana !== "pendientes") {
            setModoDescartar(false);
            setSeleccionDescartar(new Set());
        }
    }, [pestana]);

    /* -- catálogo ----------------------------------------------------------
       UNA petición, con `?idseguro=`. El modal viejo pedía el tarifario
       COMPLETO de las 19 aseguradoras (9.592 filas, 6.847 KB, 2,2 s medidos
       contra producción) y filtraba por aseguradora en el navegador para
       pintar 1.030. Con el parámetro que el endpoint YA aceptaba son 768 KB.
       Lo que se ve es idéntico: el filtro es el mismo, hecho donde debía. */
    const idSeguroPaciente =
        pacienteSeleccionado?.seguros?.idseguro ?? pacienteSeleccionado?.seguro_id ?? null;

    const pedirCatalogo = useCallback(async (forzar = false) => {
        if (!pacienteSeleccionado) return;
        if (!forzar) {
            const cacheado = leer(clavePeticion, "catalogo");
            if (Array.isArray(cacheado)) {
                setCatalogo(cacheado);
                pedidoCatalogoRef.current = clavePeticion;
                return;
            }
            if (cacheado === null || pedidoCatalogoRef.current === clavePeticion) return;
        }
        pedidoCatalogoRef.current = clavePeticion;
        marcarCargando("catalogo", true);
        marcarError("catalogo", null);
        const { datos, error } = await traerCatalogo(idSeguroPaciente);
        if (pedidoCatalogoRef.current !== clavePeticion) return;
        setCatalogo(datos);
        marcarError("catalogo", error);
        if (!error) guardar(clavePeticion, "catalogo", datos);
        marcarCargando("catalogo", false);
    }, [pacienteSeleccionado, idSeguroPaciente, clavePeticion, marcarCargando, marcarError]);

    useEffect(() => {
        pedidoCatalogoRef.current = null;
        setCatalogo(null);
        setSeleccion(new Set());
    }, [clavePeticion]);

    /* -- promociones -------------------------------------------------------
       CON `seguro_id`. Antes se pedían TODAS: de las 56 activas del sistema,
       48 son de PARTICULAR, 7 de LATINASALUD y 1 de CONFIAMED, así que un
       paciente de Latinasalud veía 48 promociones que no le corresponden. El
       endpoint ya aceptaba el parámetro; `ListadoPromocion` se llamaba sin él
       y `pacienteSeleccionado` estaba en las dependencias sin usarse. */
    const pedirPromociones = useCallback(async (forzar = false) => {
        if (!pacienteSeleccionado) return;
        if (!forzar) {
            const cacheado = leer(clavePeticion, "promociones");
            if (Array.isArray(cacheado)) {
                setPromociones(cacheado);
                pedidoPromosRef.current = clavePeticion;
                return;
            }
            if (cacheado === null || pedidoPromosRef.current === clavePeticion) return;
        }
        pedidoPromosRef.current = clavePeticion;
        marcarCargando("promociones", true);
        marcarError("promociones", null);
        const { datos, error } = await traerPromociones(idSeguroPaciente);
        if (pedidoPromosRef.current !== clavePeticion) return;
        setPromociones(datos);
        marcarError("promociones", error);
        if (!error) guardar(clavePeticion, "promociones", datos);
        marcarCargando("promociones", false);
    }, [pacienteSeleccionado, idSeguroPaciente, clavePeticion, marcarCargando, marcarError]);

    useEffect(() => {
        pedidoPromosRef.current = null;
        setPromociones(null);
        setPromoElegida(null);
        setPromoAbierta(null);
    }, [clavePeticion]);

    useEffect(() => {
        if (!abierto) return;
        if (pestana === "pendientes") pedirPendientes();
        if (pestana === "catalogo") pedirCatalogo();
        if (pestana === "promociones") pedirPromociones();
    }, [abierto, pestana, pedirPendientes, pedirCatalogo, pedirPromociones]);

    /* -- teclado y foco ---------------------------------------------------- */
    // Abrir desde "Catalogo" tiene que llevar a Catalogo, no a Pendientes.
    useEffect(() => {
        if (abierto) setPestana(pestanaInicial);
    }, [abierto, pestanaInicial]);

    useEffect(() => {
        if (!abierto) return undefined;
        origenFocoRef.current = document.activeElement;
        // El foco entra en el buscador: es lo primero que se usa.
        const t = setTimeout(() => buscadorRef.current?.focus(), 0);
        const alPulsar = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onCerrar();
            }
        };
        document.addEventListener("keydown", alPulsar);
        return () => {
            clearTimeout(t);
            document.removeEventListener("keydown", alPulsar);
        };
    }, [abierto, onCerrar]);

    useEffect(() => {
        if (abierto) return;
        // Al cerrarse, el foco vuelve al botón que lo abrió.
        const origen = origenFocoRef.current;
        if (origen && typeof origen.focus === "function") origen.focus();
    }, [abierto]);

    /* -- lo que se ve ------------------------------------------------------ */
    const esCatalogo = pestana === "catalogo";
    const listaCruda = useMemo(
        () => (esCatalogo ? catalogo || [] : pendientes || []),
        [esCatalogo, catalogo, pendientes]
    );
    const visibles = useMemo(() => {
        let filas = filtrarPorCategoria(listaCruda, categoria);
        // El tipo se filtra en memoria, así que aquí SÍ se puede ofrecer (en
        // la pantalla de Tarifarios no, porque allí la lista está paginada en
        // el servidor y el endpoint no acepta el parámetro).
        if (esCatalogo) filas = filtrarPorTipo(filas, tipo);
        return filtrarPorTexto(filas, busqueda);
    }, [listaCruda, categoria, tipo, busqueda, esCatalogo]);

    const idsEnFactura = useMemo(
        () => new Set(enFactura.map((p) => p.idprocedimiento_cita_paciente)),
        [enFactura]
    );
    // Los exámenes que ya están en la factura, para la etiqueta informativa
    // del catálogo. `idExamenTipo` es lo único que comparten una línea del
    // tarifario y una prestación ya agregada.
    const idsExamenEnFactura = useMemo(
        () => new Set(enFactura.map((p) => p.idExamenTipo)),
        [enFactura]
    );

    const estaDentro = useCallback((item) => idsEnFactura.has(idPendiente(item)), [idsEnFactura]);
    const estaSeleccionado = useCallback((item) => seleccion.has(idTarifa(item)), [seleccion]);
    const estaParaDescartar = useCallback(
        (item) => seleccionDescartar.has(idPendiente(item)),
        [seleccionDescartar]
    );

    const masiva = useMemo(
        () =>
            accionMasiva(
                visibles,
                esCatalogo ? estaSeleccionado : modoDescartar ? estaParaDescartar : estaDentro
            ),
        [visibles, esCatalogo, modoDescartar, estaSeleccionado, estaParaDescartar, estaDentro]
    );

    /* Los elegidos para descartar, podados contra lo que sigue en la lista: si
       una fila desaparece (se descartó, o cambió el paciente), su id no puede
       quedarse contando en "Descartar 100". */
    const elegidosParaDescartar = useMemo(
        () => (pendientes || []).filter((p) => seleccionDescartar.has(idPendiente(p))),
        [pendientes, seleccionDescartar]
    );

    const tiposDisponibles = useMemo(() => tiposDe(catalogo || []), [catalogo]);

    const esPromociones = pestana === "promociones";
    const promosCrudas = useMemo(() => promociones || [], [promociones]);
    const promosVisibles = useMemo(
        () => filtrarPromociones(promosCrudas, busqueda),
        [promosCrudas, busqueda]
    );
    const promoSeleccionada = useMemo(
        () => promosCrudas.find((p) => idPromocion(p) === promoElegida) || null,
        [promosCrudas, promoElegida]
    );
    const seleccionadas = useMemo(
        () => (catalogo || []).filter((item) => seleccion.has(idTarifa(item))),
        [catalogo, seleccion]
    );

    /* -- PENDIENTES: agregar y quitar -------------------------------------- */
    // Marcar = agregar. Es una sola acción de redux, la misma que despachaba
    // el modal viejo, y no toca el servidor.
    const alternarPendiente = (item) => {
        if (estaDentro(item)) dispatch(deleteProcedimientoFactura(idPendiente(item)));
        else dispatch(agregarProcedimientos([item]));
    };

    /* -- CATÁLOGO: seleccionar (local) ------------------------------------- */
    const alternarSeleccion = (item) => {
        setSeleccion((previa) => {
            const siguiente = new Set(previa);
            const id = idTarifa(item);
            if (siguiente.has(id)) siguiente.delete(id);
            else siguiente.add(id);
            return siguiente;
        });
    };

    const alternarParaDescartar = (item) => {
        setSeleccionDescartar((previa) => {
            const siguiente = new Set(previa);
            const id = idPendiente(item);
            if (siguiente.has(id)) siguiente.delete(id);
            else siguiente.add(id);
            return siguiente;
        });
    };

    const alternarUno = esCatalogo
        ? alternarSeleccion
        : modoDescartar
        ? alternarParaDescartar
        : alternarPendiente;

    // La franja masiva actúa sobre TODOS los resultados del filtro, no sobre
    // lo que se ve. En Pendientes agrega o quita de la factura; en Catálogo
    // sólo marca o desmarca, que es lo único que hace una casilla allí.
    const alternarTodosLosVisibles = () => {
        if (!masiva) return;
        if (modoDescartar && !esCatalogo) {
            setSeleccionDescartar((previa) => {
                const siguiente = new Set(previa);
                if (masiva.modo === "quitar") visibles.forEach((i) => siguiente.delete(idPendiente(i)));
                else visibles.forEach((i) => siguiente.add(idPendiente(i)));
                return siguiente;
            });
            return;
        }
        if (esCatalogo) {
            setSeleccion((previa) => {
                const siguiente = new Set(previa);
                if (masiva.modo === "quitar") visibles.forEach((i) => siguiente.delete(idTarifa(i)));
                else visibles.forEach((i) => siguiente.add(idTarifa(i)));
                return siguiente;
            });
            return;
        }
        if (masiva.modo === "quitar") {
            visibles.forEach((item) => dispatch(deleteProcedimientoFactura(idPendiente(item))));
        } else {
            // Los que ya estaban dentro no se vuelven a agregar
            // (`SET_PROCEDIMIENTOS_FACTURA` concatena sin deduplicar, así que
            // despacharlos otra vez los duplicaría).
            dispatch(agregarProcedimientos(masiva.faltan));
        }
    };

    /* -- CATÁLOGO: la ÚNICA escritura de todo el panel ---------------------
       Una sola llamada con todo lo marcado, con el MISMO cuerpo que enviaba el
       modal viejo (ver payloadProcedimientosNuevos). Crea las prestaciones en
       la base y mete en la factura lo que devuelve el servidor -- no lo que
       teníamos seleccionado: el backend puede reutilizar un "Previsto" ya
       existente en vez de crear uno nuevo, y la factura tiene que llevar esa
       fila y no una inventada aquí. */
    const agregarSeleccionados = async () => {
        if (seleccionadas.length === 0 || !pacienteSeleccionado) return;
        setAgregando(true);
        const res = await ProcedimientosNuevosBilling(
            payloadProcedimientosNuevos(pacienteSeleccionado.value, seleccionadas)
        );
        setAgregando(false);
        if (!res?.success) {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo agregar los procedimientos." });
            return;
        }
        const creadas = res.data || [];
        dispatch(agregarProcedimientos(creadas));
        setSeleccion(new Set());
        // Acabamos de CREAR prestaciones en la base: la lista de pendientes
        // que teníamos cacheada se quedó vieja. Sin esto, abrir Pendientes
        // después de agregar del catálogo mostraría una lista sin esas filas.
        invalidar(clavePeticion, "pendientes");
        pedidoRef.current = null;
        setPendientes(null);
        mostrar({
            tipo: "exito",
            texto: `Se ${creadas.length === 1 ? "agregó" : "agregaron"} ${creadas.length} procedimiento${creadas.length === 1 ? "" : "s"}`,
        });
    };

    /* -- PROMOCIONES: la otra escritura, con su propio contrato -------------
       Una sola llamada al mismo endpoint, con el cuerpo que enviaba el modal
       viejo: el examen en `id_tipo_examen`, el precio en `precio_promocion`,
       el seguro de la CABECERA de la promoción y `factura_promocion: 1`. */
    const agregarPromocion = async () => {
        if (!promoSeleccionada || !pacienteSeleccionado) return;
        const cuantos = detallesDe(promoSeleccionada).length;
        if (cuantos === 0) {
            mostrar({ tipo: "aviso", texto: "Esta promoción no tiene procedimientos." });
            return;
        }
        setAgregando(true);
        const res = await ProcedimientosNuevosBilling(
            payloadPromocion(pacienteSeleccionado.value, promoSeleccionada)
        );
        setAgregando(false);
        if (!res?.success) {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo agregar la promoción." });
            return;
        }
        const creadas = res.data || [];
        dispatch(agregarProcedimientos(creadas));
        setPromoElegida(null);
        // Mismo motivo que en el catálogo: se han creado prestaciones.
        invalidar(clavePeticion, "pendientes");
        pedidoRef.current = null;
        setPendientes(null);
        mostrar({
            tipo: "exito",
            texto: `Se ${creadas.length === 1 ? "agregó" : "agregaron"} ${creadas.length} procedimiento${creadas.length === 1 ? "" : "s"} de la promoción`,
        });
    };

    /* -- descartar un pendiente ---------------------------------------------
       "El paciente ya dijo que no se va a hacer la eco": deja de estar por
       cobrar. NO es lo mismo que quitarlo de la factura.

       El backend hace `UPDATE procedimiento_cita_paciente SET estado =
       'Eliminado'` (ProceduresDelete): la fila NO se borra, la cita NO se
       toca, y el procedimiento simplemente deja de aparecer como pendiente.
       Mismo endpoint y mismo argumento que usaba el modal viejo. */
    const descartarPendiente = async () => {
        const item = porDescartar;
        if (!item) return;
        setDescartando(true);
        const res = await ProcedimientosDeleteBilling(idPendiente(item));
        setDescartando(false);
        setPorDescartar(null);
        if (!res?.success) {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo descartar el procedimiento." });
            return;
        }
        // Fuera de la lista y de la cache: ya no esta por cobrar.
        setPendientes((previa) => (previa || []).filter((p) => idPendiente(p) !== idPendiente(item)));
        invalidar(clavePeticion, "pendientes");
        pedidoRef.current = null;
        // Y si estaba en la factura, fuera tambien: una linea que apunta a una
        // prestacion "Eliminado" no se puede facturar, asi que dejarla ahi
        // seria una factura que falla al emitir.
        if (estaDentro(item)) dispatch(deleteProcedimientoFactura(idPendiente(item)));
        mostrar({ tipo: "exito", texto: res.message || "Procedimiento descartado" });
    };

    /* -- refrescar -----------------------------------------------------------
       El peaje de la cache: si alguien crea una promocion (o carga un
       tarifario) en OTRA pestana del navegador mientras esta factura sigue
       abierta, el panel seguiria mostrando lo que trajo al elegir el paciente.
       Cerrar y reabrir no lo arregla -- eso es justo lo que la cache evita.

       Refresca la pestana que se esta mirando, no las tres: es UNA peticion,
       la de lo que tienes delante, y no 2,4 MB por pulsar un boton. */
    const refrescar = () => {
        invalidar(clavePeticion, pestana);
        if (esCatalogo) {
            pedidoCatalogoRef.current = null;
            pedirCatalogo(true);
        } else if (esPromociones) {
            pedidoPromosRef.current = null;
            pedirPromociones(true);
        } else {
            pedidoRef.current = null;
            pedirPendientes(true);
        }
    };

    /* -- descartar en lote --------------------------------------------------
       Actua sobre TODOS los resultados del filtro, igual que la franja de
       agregar: el filtro ES el mecanismo de seleccion en este panel. Sin
       filtro son los 382; con "eco" puesto, los 6 de eco.

       Concurrencia de 4: con 382 peticiones, lanzarlas todas de golpe tumba
       al navegador y probablemente al servidor. De una en una son 382 viajes
       en serie. Cuatro a la vez es el compromiso, y el progreso se ve.

       Si alguna falla, se dice CUANTAS y las que fallaron se quedan en la
       lista: nunca se finge que se descarto algo que sigue pendiente. */
    const CONCURRENCIA_LOTE = 4;

    const descartarLote = async () => {
        const items = loteDescartar || [];
        if (items.length === 0) return;
        setProgresoLote({ hechos: 0, total: items.length, fallos: 0 });

        // Se acumulan en ARRAYS y no en un contador mutable: una clausura
        // dentro del bucle sobre `let fallos += 1` es exactamente el patron que
        // acaba leyendo un valor de otra iteracion.
        const logrados = [];
        const fallidos = [];
        const cola = [...items];

        const trabajador = async () => {
            for (let item = cola.shift(); item !== undefined; item = cola.shift()) {
                // eslint-disable-next-line no-await-in-loop
                const res = await ProcedimientosDeleteBilling(idPendiente(item));
                if (res?.success) logrados.push(item);
                else fallidos.push(item);
                setProgresoLote((p) =>
                    p
                        ? { ...p, hechos: logrados.length + fallidos.length, fallos: fallidos.length }
                        : p
                );
            }
        };

        await Promise.all(
            Array.from({ length: Math.min(CONCURRENCIA_LOTE, items.length) }, trabajador)
        );
        const fallos = fallidos.length;

        const idsLogrados = new Set(logrados.map(idPendiente));
        setPendientes((previa) => (previa || []).filter((p) => !idsLogrados.has(idPendiente(p))));
        invalidar(clavePeticion, "pendientes");
        pedidoRef.current = null;
        // Las que estaban en la factura salen tambien: una linea que apunta a
        // una prestacion "Eliminado" no se puede facturar.
        logrados.forEach((item) => {
            if (idsEnFactura.has(idPendiente(item))) {
                dispatch(deleteProcedimientoFactura(idPendiente(item)));
            }
        });

        setProgresoLote(null);
        setLoteDescartar(null);
        setSeleccionDescartar(new Set());
        setModoDescartar(false);
        mostrar({
            tipo: fallos > 0 ? "aviso" : "exito",
            texto:
                fallos > 0
                    ? `Se descartaron ${logrados.length} de ${items.length}. ${fallos} no se pudieron descartar y siguen pendientes.`
                    : `Se ${logrados.length === 1 ? "descartó" : "descartaron"} ${logrados.length} procedimiento${logrados.length === 1 ? "" : "s"}`,
        });
    };

    const limpiarFiltros = () => {
        setBusqueda("");
        setCategoria("");
        setTipo("");
    };

    if (!abierto) return null;

    const titular = pacienteSeleccionado?.label || seguroSeleccionado?.label || "";
    const tarifario = pacienteSeleccionado?.seguros?.nombre || seguroSeleccionado?.label || null;
    const totalEnFactura = enFactura.length;
    const importeEnFactura = importeDe(enFactura);
    const listaPintada = esPromociones ? promosVisibles : visibles;
    const totalPintable = esPromociones ? promosCrudas.length : listaCruda.length;
    const contador = contadorCoincidencias(busqueda, listaPintada.length, totalPintable);
    const cargandoAhora = Boolean(cargando[pestana]);
    const errorAhora = error[pestana] || null;
    const hayFiltro = Boolean(busqueda.trim()) || categoria !== "" || tipo !== "";

    return (
        <div
            className="pag"
            role="dialog"
            aria-modal="false"
            aria-label="Agregar procedimientos"
            ref={cajaRef}
            data-testid="panel-agregar"
        >
            {/* -- cabecera -- */}
            <div className="pag__cabecera">
                <div className="pag__titulo">
                    <span className="pag__titulo-h">Agregar procedimientos</span>
                    <span className="pag__titulo-sub">
                        {tituloClinico(titular)}
                        {tarifario ? ` · tarifario ${tituloClinico(tarifario)}` : ""}
                    </span>
                </div>
                <button
                    type="button"
                    className="pag__cerrar"
                    onClick={refrescar}
                    disabled={cargandoAhora}
                    title={`Volver a pedir ${ETIQUETA_PESTANA[pestana]} al servidor`}
                    aria-label={`Refrescar ${ETIQUETA_PESTANA[pestana]}`}
                    data-testid="panel-refrescar"
                >
                    <IconoRefrescar />
                </button>
                <button
                    type="button"
                    className="pag__cerrar"
                    onClick={onCerrar}
                    aria-label="Cerrar el panel"
                    data-testid="panel-cerrar"
                >
                    <IconoCerrar />
                </button>
            </div>

            {/* -- pestañas. Cambiar de pestaña NO pierde nada: lo agregado
                   sigue agregado, porque vive en la factura, no aquí. -- */}
            <div className="pag__pestanas" role="tablist" aria-label="Origen de los procedimientos">
                {PESTANAS.map((p) => {
                    const activa = pestana === p.id;
                    // Sólo se pinta un número cuando SALE de una respuesta.
                    // Un "(890)" inventado es peor que no poner nada.
                    const cuenta =
                        p.id === "pendientes"
                            ? pendientes?.length ?? null
                            : p.id === "catalogo"
                            ? catalogo?.length ?? null
                            : promociones?.length ?? null;
                    const disponible = true;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            role="tab"
                            aria-selected={activa}
                            className={`pag__pestana${activa ? " is-activa" : ""}`}
                            onClick={() => setPestana(p.id)}
                            data-testid={`panel-pestana-${p.id}`}
                        >
                            {p.etiqueta}
                            {cuenta !== null && <span className="pag__cuenta">{cuenta}</span>}
                            {/* Sin número inventado en las pestañas que aún no
                                traen datos: un "(890)" que no sale de ninguna
                                respuesta es peor que no poner nada. */}
                            {!disponible && <span className="pag__cuenta">pronto</span>}
                        </button>
                    );
                })}
            </div>

            {(
                <>
                    {/* -- buscador y filtros -- */}
                    <div className="pag__filtros">
                        <div className="pag__buscador">
                            <IconoBuscar />
                            <input
                                ref={buscadorRef}
                                type="text"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                // Enter no envía nada: no hay formulario que
                                // enviar y aquí un submit accidental cerraría
                                // el panel o emitiría la factura.
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") e.preventDefault();
                                }}
                                placeholder="Buscar por código o descripción"
                                aria-label="Buscar procedimiento"
                                data-testid="panel-buscar"
                            />
                            {contador && (
                                <span className="pag__contador" data-testid="panel-contador">{contador}</span>
                            )}
                            {hayFiltro && (
                                <button
                                    type="button"
                                    className="pag__limpiar"
                                    onClick={limpiarFiltros}
                                    aria-label="Limpiar la búsqueda y los filtros"
                                    data-testid="panel-limpiar"
                                >
                                    <IconoCerrar />
                                </button>
                            )}
                        </div>

                        {esCatalogo && tiposDisponibles.length > 1 && (
                            <label className={`pag__chip${tipo !== "" ? " is-activo" : ""}`}>
                                <span>{tipo !== "" ? tituloClinico(tipo) : "Tipo"}</span>
                                <select
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                    aria-label="Filtrar por tipo"
                                    data-testid="panel-tipo"
                                >
                                    <option value="">Todos los tipos</option>
                                    {tiposDisponibles.map((t) => (
                                        <option key={t} value={t}>{tituloClinico(t)}</option>
                                    ))}
                                </select>
                            </label>
                        )}

                        {!esPromociones && categorias.length > 0 && (
                            <label className={`pag__chip${categoria !== "" ? " is-activo" : ""}`}>
                                <span>
                                    {categoria !== ""
                                        ? categorias.find((c) => String(c.value) === String(categoria))?.label ||
                                          "Categoría"
                                        : "Categoría"}
                                </span>
                                <select
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                    aria-label="Filtrar por categoría"
                                    data-testid="panel-categoria"
                                >
                                    <option value="">Todas las categorías</option>
                                    {categorias.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    {/* -- franja de acción masiva. Siempre visible sobre la
                           lista, y dice el número EXACTO sobre el que actúa. -- */}
                    {/* El interruptor del modo. Se dice EXPLICITAMENTE que la
                        casilla cambia de significado, porque cambiarlo en
                        silencio es la forma mas rapida de que alguien descarte
                        algo creyendo que lo agregaba. */}
                    {!esCatalogo && !esPromociones && (
                        <div
                            className={`pag__modo${modoDescartar ? " is-activo" : ""}`}
                            data-testid="panel-modo"
                        >
                            {modoDescartar ? (
                                <>
                                    <span className="pag__modo-texto">
                                        Elige los que <b>ya no se van a hacer</b>. Marcar aquí no
                                        agrega nada a la factura.
                                    </span>
                                    <button
                                        type="button"
                                        className="pag__modo-boton"
                                        onClick={() => {
                                            setModoDescartar(false);
                                            setSeleccionDescartar(new Set());
                                        }}
                                        data-testid="panel-modo-salir"
                                    >
                                        Salir
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="pag__modo-boton"
                                    onClick={() => setModoDescartar(true)}
                                    data-testid="panel-modo-entrar"
                                >
                                    Elegir varios para descartar
                                </button>
                            )}
                        </div>
                    )}

                    {!esPromociones && masiva && (
                      <div className="pag__masiva-fila">
                        <button
                            type="button"
                            className="pag__masiva"
                            onClick={alternarTodosLosVisibles}
                            data-testid="panel-masiva"
                        >
                            <span className={`pag__check${masiva.modo === "quitar" ? " is-marcada" : ""}`} aria-hidden="true">
                                {masiva.modo === "quitar" && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                )}
                            </span>
                            <span className="pag__masiva-texto">
                                {/* En Catálogo la franja SELECCIONA: decir
                                    "Agregar" allí sería mentir, porque agregar
                                    escribe en la base y eso sólo pasa al pulsar
                                    el botón del pie. */}
                                {esCatalogo || modoDescartar
                                    ? masiva.modo === "quitar"
                                        ? "Quitar la selección de los "
                                        : "Seleccionar los "
                                    : masiva.modo === "quitar"
                                    ? "Quitar los "
                                    : "Agregar los "}
                                <strong>{masiva.cuantas}</strong>
                                {busqueda.trim() ? <> de «{busqueda.trim()}»</> : null}
                            </span>
                            <span className="pag__masiva-importe">${montoLegible(masiva.importe)}</span>
                        </button>

                        {/* Descartar en lote: la misma simetria que exige el
                            encargo, aplicada a la accion destructiva. Actua
                            sobre los resultados del FILTRO, y el numero lo
                            dice. Solo en Pendientes: en Catalogo la fila es
                            una tarifa, no una prestacion del paciente. */}
                        {!esCatalogo && (
                            <button
                                type="button"
                                className="pag__masiva-descartar"
                                onClick={() =>
                                    setLoteDescartar(modoDescartar ? elegidosParaDescartar : visibles)
                                }
                                disabled={modoDescartar && elegidosParaDescartar.length === 0}
                                title={
                                    modoDescartar
                                        ? `Descartar los ${elegidosParaDescartar.length} elegidos`
                                        : `Descartar ${visibles.length} de los pendientes del paciente`
                                }
                                data-testid="panel-masiva-descartar"
                            >
                                {modoDescartar
                                    ? `Descartar ${elegidosParaDescartar.length} elegidos`
                                    : `Descartar ${visibles.length}`}
                            </button>
                        )}
                      </div>
                    )}

                    {/* -- estados. Van FUERA del contenedor de la lista: la
                           lista virtualizada es su propio contenedor de scroll
                           y un mensaje centrado no debe vivir dentro de un
                           hueco de 1.030 filas de alto. -- */}
                    <div className="pag__estados">
                        {cargandoAhora && (
                            <div className="pag__skeleton" data-testid="panel-cargando">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div className="pag__skeleton-fila" key={i}>
                                        <span className="pag__skeleton-barra" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {!cargandoAhora && errorAhora && (
                            <div className="pag__mensaje" data-testid="panel-error">
                                <p className="pag__mensaje-titulo">No se pudo cargar</p>
                                <p>{errorAhora}</p>
                                <button type="button" className="fcrear__btn" onClick={() => (esCatalogo ? pedirCatalogo(true) : esPromociones ? pedirPromociones(true) : pedirPendientes(true))}>
                                    Reintentar
                                </button>
                            </div>
                        )}

                        {!cargandoAhora && !errorAhora && listaPintada.length === 0 && (
                            <div className="pag__mensaje" data-testid="panel-vacio">
                                {hayFiltro ? (
                                    <>
                                        <p className="pag__mensaje-titulo">Nada coincide</p>
                                        <p>
                                            Hay {totalPintable}{" "}
                                            {esCatalogo
                                                ? `procedimiento${totalPintable === 1 ? "" : "s"} en el tarifario`
                                                : esPromociones
                                                ? `promoci${totalPintable === 1 ? "ón" : "ones"}`
                                                : `pendiente${totalPintable === 1 ? "" : "s"}`}
                                            , pero ninguno con estos filtros.
                                        </p>
                                        <button type="button" className="fcrear__btn" onClick={limpiarFiltros}>
                                            Limpiar filtros
                                        </button>
                                    </>
                                ) : (
                                    esPromociones ? (
                                        <>
                                            <p className="pag__mensaje-titulo">Sin promociones</p>
                                            <p>
                                                {tarifario
                                                    ? `${tituloClinico(tarifario)} no tiene promociones activas.`
                                                    : "No hay promociones activas para esta entidad."}
                                            </p>
                                        </>
                                    ) : esCatalogo ? (
                                        <>
                                            <p className="pag__mensaje-titulo">Sin tarifario</p>
                                            <p>
                                                {tarifario
                                                    ? `${tituloClinico(tarifario)} todavía no tiene precios cargados.`
                                                    : "Este paciente no tiene una entidad con tarifario."}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="pag__mensaje-titulo">Sin procedimientos pendientes</p>
                                            <p>Este paciente no tiene nada por cobrar. Búscalo en el catálogo.</p>
                                        </>
                                    )
                                )}
                            </div>
                        )}

                    </div>

                    {!cargandoAhora && !errorAhora && esPromociones && promosVisibles.length > 0 && (
                        <div className="pag__lista">
                            {promosVisibles.map((promo) => (
                                <FilaPromocion
                                    key={idPromocion(promo)}
                                    promo={promo}
                                    // Se elige UNA: marcar otra sustituye a la
                                    // anterior, en vez de avisar después de
                                    // pulsar como hacía el modal viejo.
                                    marcada={promoElegida === idPromocion(promo)}
                                    onAlternar={(p) =>
                                        setPromoElegida((previa) =>
                                            previa === idPromocion(p) ? null : idPromocion(p)
                                        )
                                    }
                                    abierta={promoAbierta === idPromocion(promo)}
                                    onAbrir={(p) =>
                                        setPromoAbierta((previa) =>
                                            previa === idPromocion(p) ? null : idPromocion(p)
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}

                    {/* FASE 4: las listas planas van virtualizadas -- con 1.030
                        filas del tarifario, meterlas todas en el DOM hace lento
                        el scroll y, sobre todo, cada marcado. Por debajo de 60
                        filas pinta todo, así que en el caso pequeño no cambia
                        nada. Las promociones NO pasan por aquí: se expanden, y
                        esto sólo vale para filas de alto fijo. */}
                    {!cargandoAhora && !errorAhora && !esPromociones && visibles.length > 0 && (
                        <ListaVirtual
                            className="pag__lista"
                            items={visibles}
                            alto={56}
                            render={(item) =>
                                esCatalogo ? (
                                    <FilaAgregar
                                        key={idTarifa(item)}
                                        item={item}
                                        marcada={estaSeleccionado(item)}
                                        onAlternar={alternarUno}
                                        yaEnFactura={yaFacturado(item, idsExamenEnFactura)}
                                        // Una línea de tarifario no tiene orden
                                        // ni fecha: lo que ubica el examen aquí
                                        // es su tipo.
                                        columnaMedia={tituloClinico(item?.ExamenTipo?.tipo || "")}
                                    />
                                ) : (
                                    <FilaAgregar
                                        key={idPendiente(item)}
                                        item={item}
                                        marcada={modoDescartar ? estaParaDescartar(item) : estaDentro(item)}
                                        onAlternar={alternarUno}
                                        // La etiqueta sigue diciendo la verdad
                                        // en los dos modos: esto ya está en la
                                        // factura. En modo descartar es justo lo
                                        // que hay que ver antes de tirarlo.
                                        yaEnFactura={estaDentro(item)}
                                        // La papelera de fila sobra mientras se
                                        // elige en lote: dos formas de descartar
                                        // a la vez sólo confunden.
                                        onDescartar={modoDescartar ? null : setPorDescartar}
                                        paraDescartar={modoDescartar}
                                    />
                                )
                            }
                        />
                    )}
                </>
            )}

            {/* -- pie: el efecto acumulado, siempre a la vista -- */}
            <div className="pag__pie">
                <span className="pag__pie-cuenta" data-testid="panel-pie-cuenta">
                    <strong>{totalEnFactura}</strong> en la factura
                </span>
                <span className="pag__pie-sep" />
                <span className="pag__pie-importe">${montoLegible(importeEnFactura)}</span>

                {/* En Catálogo hace falta un segundo dato: lo que está marcado
                    y todavía NO ha llegado a la factura. */}
                {esCatalogo && seleccionadas.length > 0 && (
                    <>
                        <span className="pag__pie-sep" />
                        <span className="pag__pie-pendiente" data-testid="panel-pie-seleccion">
                            <strong>{seleccionadas.length}</strong> por agregar ·{" "}
                            ${montoLegible(importeDe(seleccionadas))}
                        </span>
                    </>
                )}

                {esPromociones && promoSeleccionada && (
                    <>
                        <span className="pag__pie-sep" />
                        <span className="pag__pie-pendiente" data-testid="panel-pie-promo">
                            <strong>{detallesDe(promoSeleccionada).length}</strong> por agregar ·{" "}
                            ${montoLegible(importePromocion(promoSeleccionada))}
                        </span>
                    </>
                )}

                <span className="pag__pie-hueco" />

                {/* En Pendientes "Listo" sólo cierra: cada marca ya surtió
                    efecto. En Catálogo hay UNA escritura, y su botón dice
                    exactamente cuántas prestaciones va a crear. */}
                <button type="button" className="fcrear__btn" onClick={onCerrar} data-testid="panel-listo">
                    {(esCatalogo && seleccionadas.length > 0) || (esPromociones && promoSeleccionada)
                        ? "Cancelar"
                        : "Listo"}
                </button>
                {esPromociones && (
                    <button
                        type="button"
                        className="fcrear__btn fcrear__btn--primario"
                        onClick={agregarPromocion}
                        disabled={!promoSeleccionada || agregando}
                        data-testid="panel-agregar-promocion"
                    >
                        {agregando
                            ? "Agregando…"
                            : promoSeleccionada
                            ? `Agregar ${detallesDe(promoSeleccionada).length} procedimiento${
                                  detallesDe(promoSeleccionada).length === 1 ? "" : "s"
                              }`
                            : "Agregar promoción"}
                    </button>
                )}
                {esCatalogo && (
                    <button
                        type="button"
                        className="fcrear__btn fcrear__btn--primario"
                        onClick={agregarSeleccionados}
                        disabled={seleccionadas.length === 0 || agregando}
                        data-testid="panel-agregar-seleccion"
                    >
                        {agregando
                            ? "Agregando…"
                            : seleccionadas.length === 0
                            ? "Agregar"
                            : `Agregar ${seleccionadas.length}`}
                    </button>
                )}
            </div>
            {/* Escribe en la base y no se deshace desde aqui, asi que se
                pregunta -- y se dice EXACTAMENTE que hace, porque descartar un
                pendiente y quitar una linea de la factura se parecen
                demasiado. */}
            {/* El lote es destructivo y no se deshace: la confirmacion dice el
                numero exacto y, mientras corre, el progreso -- con 382
                peticiones, una barra parada seria indistinguible de un cuelgue. */}
            <Modal
                abierto={Boolean(loteDescartar)}
                titulo="Descartar procedimientos"
                destructivo
                onCerrar={() => (progresoLote ? null : setLoteDescartar(null))}
                secundario={progresoLote ? undefined : { label: "Cancelar" }}
                primario={
                    progresoLote
                        ? undefined
                        : {
                              label: `Sí, descartar ${(loteDescartar || []).length}`,
                              onClick: descartarLote,
                          }
                }
            >
                {progresoLote ? (
                    <p data-testid="panel-lote-progreso">
                        Descartando… <b>{progresoLote.hechos}</b> de {progresoLote.total}
                        {progresoLote.fallos > 0 && ` · ${progresoLote.fallos} con error`}
                    </p>
                ) : (
                    <>
                        <p>
                            <b>{(loteDescartar || []).length}</b> procedimiento
                            {(loteDescartar || []).length === 1 ? "" : "s"} dejarán de estar
                            pendientes de cobro para este paciente
                            {hayFiltro ? " (los que coinciden con los filtros puestos)" : ""}.
                        </p>
                        <p className="pag__nota">
                            Úsalo cuando esos procedimientos ya no se vayan a hacer. Las órdenes de
                            las citas no se borran y las facturas ya emitidas no cambian. No se
                            puede deshacer desde esta pantalla.
                        </p>
                    </>
                )}
            </Modal>

            <Modal
                abierto={Boolean(porDescartar)}
                titulo="Descartar el procedimiento"
                destructivo
                onCerrar={() => setPorDescartar(null)}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: "Sí, descartar",
                    cargando: descartando,
                    onClick: descartarPendiente,
                }}
            >
                <p>
                    <b>{tituloClinico(porDescartar?.ExamenTipo?.descripcion || "")}</b> dejará de
                    estar pendiente de cobro para este paciente.
                </p>
                <p className="pag__nota">
                    Úsalo cuando el procedimiento ya no se va a hacer. La orden de la cita no se
                    borra y las facturas ya emitidas no cambian; sólo deja de aparecer aquí. No se
                    puede deshacer desde esta pantalla.
                </p>
            </Modal>
        </div>
    );
}
